import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addClient,
  addAgreement,
  commitClientImport,
  commitAgreementImport,
  validateAgreementRows,
  commitAgreementRows,
  getMerchants,
  validateClientImport,
  validateAgreementImport,
  getImportedAgreement,
  updateImportedAgreement,
  getProductOptions,
  getBrands,
  getBrandModels,
  createBrand,
  createModel,
  getColors,
  fetchImportHistory,
  discardAgreement,
  confirmAgreement,
} from "./api";
import { listCustomers } from "@/features/customers/api";
import type { ManualClient, AgreementRowInput, AddAgreementPayload } from "./types";

// Query key factory for the import feature.
// Exported so the catalogue feature can reuse the SAME key tuples for invalidation.
export const keys = {
  merchants: (search?: string) => ["support", "merchants", search ?? ""] as const,
  agreementDetail: (loanId: number | null) =>
    ["support", "agreements", loanId] as const,
  productOptions: (type: "car" | "technology" | null) =>
    ["support", "agreements", "product-options", type] as const,
  brands: (type: "car" | "technology") =>
    ["support", "agreements", "brands", type] as const,
  brandModels: (type: "car" | "technology", brandId: number | null) =>
    ["support", "agreements", "brand-models", type, brandId] as const,
  // Import history: scoped to one merchant (or all when merchantId is undefined).
  importHistory: (merchantId?: number) =>
    ["support", "agreements", "import-history", merchantId ?? null] as const,
};

export function useMerchants(search?: string) {
  return useQuery({
    queryKey: keys.merchants(search),
    queryFn: () => getMerchants(search),
    placeholderData: (prev) => prev,
  });
}

export function useValidateImport() {
  return useMutation({
    mutationFn: ({ merchantId, file }: { merchantId: number; file: File }) =>
      validateClientImport(merchantId, file),
  });
}

export function useCommitImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ merchantId, file }: { merchantId: number; file: File }) =>
      commitClientImport(merchantId, file),
    onSuccess: () => {
      // Refresh the customer list so newly imported clients appear immediately.
      qc.invalidateQueries({ queryKey: ["support", "customers"] });
    },
  });
}

// ── Agreement import hooks ────────────────────────────────────────────────────

export function useValidateAgreementImport() {
  return useMutation({
    mutationFn: ({ merchantId, file }: { merchantId: number; file: File }) =>
      validateAgreementImport(merchantId, file),
  });
}

export function useCommitAgreementImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ merchantId, file }: { merchantId: number; file: File }) =>
      commitAgreementImport(merchantId, file),
    onSuccess: () => {
      // Refresh the customer list — newly imported agreements may affect customer records.
      qc.invalidateQueries({ queryKey: ["support", "customers"] });
    },
  });
}

// ── Agreement import — editable rows hooks (subtask 06) ───────────────────────

/** Re-validate hand-edited rows (debounced by the caller). No cache invalidation — preview only. */
export function useValidateAgreementRows() {
  return useMutation({
    mutationFn: ({ merchantId, rows }: { merchantId: number; rows: AgreementRowInput[] }) =>
      validateAgreementRows(merchantId, rows),
  });
}

/** Commit hand-edited rows — same side-effects as the file commit (refresh customer list). */
export function useCommitAgreementRows() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ merchantId, rows }: { merchantId: number; rows: AgreementRowInput[] }) =>
      commitAgreementRows(merchantId, rows),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["support", "customers"] });
    },
  });
}

// ── Agreement detail / edit hooks ─────────────────────────────────────────────

/**
 * Fetches full detail for one imported agreement.
 * Only fires when loanId is not null (controlled by the edit drawer's open state).
 */
export function useImportedAgreement(loanId: number | null) {
  return useQuery({
    queryKey: keys.agreementDetail(loanId),
    queryFn: () => getImportedAgreement(loanId!),
    enabled: loanId !== null,
    staleTime: 0, // always re-fetch on drawer open so missing count is fresh
  });
}

/**
 * Sends PATCH to update editable detail fields.
 * On success, invalidates the detail query so the drawer refreshes,
 * and also updates the caller's local list via the returned data.
 */
export function useUpdateImportedAgreement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      loanId,
      additional_data,
    }: {
      loanId: number;
      additional_data: Record<string, string>;
    }) => updateImportedAgreement(loanId, additional_data),
    onSuccess: (_data, { loanId }) => {
      // Invalidate so any other consumer of the detail query re-fetches.
      qc.invalidateQueries({ queryKey: keys.agreementDetail(loanId) });
    },
  });
}

/**
 * Fetches canonical product names for the edit-drawer catalogue dropdown.
 * Only fires when `drawerOpen` is true and type is "car" or "technology".
 * Callers pass the loan type from the detail record.
 */
export function useProductOptions(
  type: "car" | "technology" | null,
  drawerOpen: boolean,
) {
  return useQuery({
    queryKey: keys.productOptions(type),
    queryFn: () => getProductOptions(type!),
    enabled: drawerOpen && (type === "car" || type === "technology"),
    // Options are stable; cache for 5 minutes to avoid repeat fetches while drawer is open.
    staleTime: 5 * 60 * 1000,
  });
}

// ── Color catalogue options hook ──────────────────────────────────────────────

/**
 * Global colour options for the detail colour dropdown.
 * Static-ish list (rarely changes); cached for 60s to avoid repeat fetches.
 * Query key is stable so subtask 05's catalogue CRUD can invalidate it by key.
 */
export function useColorOptions(enabled = true) {
  return useQuery({
    queryKey: ["support", "agreements", "colors"] as const,
    queryFn: getColors,
    enabled,
    staleTime: 60 * 1000,
  });
}

// ── Inline catalogue-add hooks (subtask 08) ───────────────────────────────────

/** Brands for the modal's step-1 select. Only fetches while the modal is open with a valid type. */
export function useBrands(type: "car" | "technology", enabled: boolean) {
  return useQuery({
    queryKey: keys.brands(type),
    queryFn: () => getBrands(type),
    enabled,
    staleTime: 60 * 1000,
  });
}

/** Models under the chosen brand (step-2 select). Fires once a brand is selected. */
export function useBrandModels(
  type: "car" | "technology",
  brandId: number | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: keys.brandModels(type, brandId),
    queryFn: () => getBrandModels(type, brandId!),
    enabled: enabled && brandId !== null,
    staleTime: 60 * 1000,
  });
}

/** Add a brand; on success refresh the brand list so the new row is selectable immediately. */
export function useCreateBrand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ type, name }: { type: "car" | "technology"; name: string }) =>
      createBrand(type, name),
    onSuccess: (_data, { type }) => {
      qc.invalidateQueries({ queryKey: keys.brands(type) });
    },
  });
}

/** Add a model; on success refresh that brand's model list. */
export function useCreateModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      type,
      brandId,
      name,
    }: {
      type: "car" | "technology";
      brandId: number;
      name: string;
    }) => createModel(type, brandId, name),
    onSuccess: (_data, { type, brandId }) => {
      qc.invalidateQueries({ queryKey: keys.brandModels(type, brandId) });
    },
  });
}

export function useAddClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ merchantId, payload }: { merchantId: number; payload: ManualClient }) =>
      addClient(merchantId, payload),
    onSuccess: () => {
      // Refresh the customer list after a successful manual add.
      qc.invalidateQueries({ queryKey: ["support", "customers"] });
    },
    // ImportError is rethrown — the component catches it for inline field errors.
  });
}

/**
 * Adds a single agreement manually (0119) — the "Ko'rib chiqish" / review step. This only CREATES the
 * loan so its contract PDF can be rendered; the merchant push + import-history "added" row are deferred
 * to the confirm step (0127, see useConfirmAgreement). So there is intentionally NO cache invalidation
 * here: an unconfirmed add must not surface in the customer list or import history until the operator
 * confirms (or it gets discarded on cancel). ImportError is rethrown — the drawer catches it for inline
 * field / agreement-level errors.
 */
export function useAddAgreement() {
  return useMutation({
    mutationFn: ({
      merchantId,
      payload,
    }: {
      merchantId: number;
      payload: AddAgreementPayload;
    }) => addAgreement(merchantId, payload),
  });
}

/**
 * Confirms a hand-added agreement after the operator reviewed its contract PDF (0127). This is the step
 * that actually fires the merchant push and writes the import-history "added" row server-side, so it is
 * the point at which we refresh the customer list + the merchant's import history. Idempotent backend.
 */
export function useConfirmAgreement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ loanId }: { loanId: number; merchantId: number }) => confirmAgreement(loanId),
    onSuccess: (_data, { merchantId }) => {
      qc.invalidateQueries({ queryKey: ["support", "customers"] });
      qc.invalidateQueries({ queryKey: keys.importHistory(merchantId) });
    },
  });
}

/**
 * Rolls back a not-yet-confirmed add (0122): hard-deletes the Loan the contract-preview POST created
 * when the operator cancels/edits instead of confirming. On success the just-created loan no longer
 * exists, so refresh the customer list + the merchant's import history (the original add invalidated
 * them; the rollback must un-do that view). Errors are swallowed by the caller — a failed rollback
 * leaves an orphan imported loan that the operator can delete from the catalogue, never blocks the UI.
 */
export function useDiscardAgreement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ loanId }: { loanId: number; merchantId: number }) => discardAgreement(loanId),
    onSuccess: (_data, { merchantId }) => {
      qc.invalidateQueries({ queryKey: ["support", "customers"] });
      qc.invalidateQueries({ queryKey: keys.importHistory(merchantId) });
    },
  });
}

/**
 * Searches existing customers for the add-agreement drawer's "existing customer" branch.
 * Reuses the customers feature's listCustomers (GET /support/customers/?search=). There is NO
 * merchant filter — the backend's add-agreement guard authoritatively rejects a client that does
 * not belong to the selected merchant, so the picker shows phone numbers to help the operator pick
 * the right person. Only fires when enabled (drawer open + existing branch) and a query is present.
 */
export function useCustomerSearch(search: string, enabled: boolean) {
  const q = search.trim();
  return useQuery({
    queryKey: ["support", "customers", "agreement-picker", q],
    queryFn: () => listCustomers({ search: q, status: "all", page: 1 }),
    enabled: enabled && q.length > 0,
    placeholderData: (prev) => prev,
    staleTime: 30 * 1000,
  });
}

// ── Import history hook ───────────────────────────────────────────────────────

/**
 * Fetches recent import history entries for one merchant (or all when merchantId is undefined).
 * Capped at 50 entries, ordered by -timestamp. Stale after 30 s.
 */
export function useImportHistory(merchantId?: number) {
  return useQuery({
    queryKey: keys.importHistory(merchantId),
    queryFn: () => fetchImportHistory(merchantId),
    staleTime: 30 * 1000,
  });
}
